/**
 * Session state.
 *
 * The token in localStorage is a claim, not proof — it may be expired or
 * revoked — so boot always re-validates it against /api/auth/me before showing
 * the app. `capabilities` here only decides what to render; the server
 * enforces the same rules independently.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  api,
  isAbort,
  setToken,
  getToken,
  setSessionEndListener,
  type SessionEnd,
} from "../api/client";
import type { User } from "../api/types";
import { track } from "../lib/analytics";
import { forgetAttribution, storedAttribution } from "../lib/attribution";

interface AuthState {
  user: User | null;
  ready: boolean;
  /**
   * Set when the server refused our token rather than the user signing out.
   * Null for a visitor who simply is not signed in — the difference is what the
   * form has to explain.
   */
  sessionEnd: SessionEnd | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    acceptedAgreement: boolean,
  ) => Promise<void>;
  /** Sign in with a Google ID token from Google Identity Services. */
  signInWithGoogle: (credential: string) => Promise<void>;
  signOut: () => void;
  /** Re-reads the user, e.g. after returning from Stripe checkout. */
  refresh: () => Promise<void>;
  /** Records acceptance of the current agreement version. */
  acceptAgreement: (version: string) => Promise<void>;
  /** Replace the cached user after a change the server already applied. */
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [sessionEnd, setSessionEnd] = useState<SessionEnd | null>(null);

  // Sessions now end while the app is open — a 1-day token expires overnight,
  // and signing in on a second device kills this one immediately. Without this,
  // both cases left the app signed in as far as React knew, failing every
  // request against a token the server had already refused.
  useEffect(() => {
    setSessionEndListener((reason) => {
      setSessionEnd(reason);
      setUser(null);
    });
    return () => setSessionEndListener(null);
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setReady(true);
      return;
    }
    const controller = new AbortController();
    api
      .me(controller.signal)
      .then((me) => {
        setUser(me);
        setReady(true);
      })
      .catch((cause: unknown) => {
        // An aborted request is not a refused credential — `useRows`' rule,
        // and this is the one place that had missed it. React runs an effect
        // twice in development and aborts the first run's request in between;
        // read as a failure, that dropped the token the *second* call was
        // about to validate, so the app signed itself out holding a perfectly
        // good session and every later request went out unauthenticated. The
        // page it showed for that was the report archive answering "Sign in to
        // continue" — which is where Stripe returns a buyer.
        if (isAbort(cause)) return;
        setToken(null); // stale or forged; start signed out
        setReady(true);
      });
    return () => controller.abort();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { token, user: signedIn } = await api.login(email, password);
    setToken(token);
    setUser(signedIn);
    // Signing in answers whatever the previous session's notice was about, so
    // it must not survive into the new one.
    setSessionEnd(null);
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, acceptedAgreement: boolean) => {
      const { token, user: created } = await api.signup(
        email,
        password,
        acceptedAgreement,
        storedAttribution(),
      );
      setToken(token);
      setUser(created);
      setSessionEnd(null);
      // The record is on the account now; the ad platforms hear about it
      // here, once, from the one place that knows an account was created.
      forgetAttribution();
      track({ name: "sign_up", method: "password" });
    },
    [],
  );

  const signInWithGoogle = useCallback(async (credential: string) => {
    const { token, user: signedIn, created } = await api.google(
      credential,
      storedAttribution(),
    );
    setToken(token);
    setUser(signedIn);
    setSessionEnd(null);
    if (created) {
      forgetAttribution();
      track({ name: "sign_up", method: "google" });
    }
  }, []);

  const acceptAgreement = useCallback(async (version: string) => {
    setUser(await api.acceptAgreement(version));
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
    // Asked for, so there is nothing to explain.
    setSessionEnd(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try {
      setUser(await api.me());
    } catch {
      setToken(null);
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, ready, sessionEnd, signIn, signUp, signInWithGoogle,
             signOut, refresh, acceptAgreement, setUser }),
    [user, ready, sessionEnd, signIn, signUp, signInWithGoogle, signOut,
     refresh, acceptAgreement],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
}
